import React from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "./atoms";
import { useLifeAreas } from "./config";
import { timeAgo } from "./lifeAreasTheme";
import { fs, T, TONE_COLOR } from "./tokens";
import { DimensionKey, RingInsight } from "./types";

// Spec organism AstriaInsightPanel (compact): latest insights as rows. How each
// insight type is labelled and toned comes from the config.
const InsightPanel: React.FC<{
  insights: RingInsight[];
  onSelectArea: (key: DimensionKey) => void;
}> = ({ insights, onSelectArea }) => {
  const L = useLifeAreas();
  const { t } = useTranslation();
  if (!insights.length) return null;
  return (
    <section aria-label={t("lifeAreas.insights")} style={{ marginTop: 24 }}>
      <h3 style={{ ...fs("base", 600), margin: "0 0 10px" }}>{t("lifeAreas.insights")}</h3>
      <ul style={{ margin: 0, padding: 0, display: "grid", gap: 8 }}>
        {insights.slice(0, 3).map((i) => {
          const area = L.area(i.dimensionKey);
          const kind = L.config.insightTypes[i.type] ?? { label: i.type, tone: "info" as const };
          return (
            <li key={i.id} style={{ listStyle: "none" }}>
              <button
                type="button"
                className="lg2-row"
                onClick={() => onSelectArea(i.dimensionKey)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  width: "100%",
                  textAlign: "left",
                  padding: "10px 14px",
                  background: T.color.bg.card,
                  border: `1px solid ${T.color.bg.overlay}`,
                  borderRadius: T.radius.xl,
                  color: T.color.text.primary,
                  cursor: "pointer",
                }}
              >
                <span aria-hidden="true" style={{ fontSize: 20 }}>
                  {area.emoji}
                </span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ ...fs("sm", 600), display: "block" }}>{L.insightText(i).headline}</span>
                  <span style={{ ...fs("xs"), display: "block", color: T.color.text.secondary }}>
                    {t("lifeAreas.confidence", { pct: Math.round(i.confidence * 100) })}
                    {i.generatedAt ? ` · ${timeAgo(i.generatedAt)}` : ""}
                  </span>
                </span>
                <Badge color={TONE_COLOR[kind.tone] ?? T.color.text.secondary}>{kind.label}</Badge>
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
};

export default InsightPanel;
